import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Typography } from "src/UILibrary"
import { useRecoilState } from "recoil"
import Stepper from "src/components/atom/Stepper"
import { GENDER, MEMBER_TYPE, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { ShowToast } from "src/states/showToast"
import { AuthenticationService, Career, ProfileService } from "src/openapi"
import { AddressService } from "src/openapi_address"
import { Dayjs } from "dayjs"
import {
  validateGeneralInformation,
  validateLoginInformation,
  validatePRInformation,
} from "../AdviserValidation"
import { ShowHeader } from "src/states/showHeader"
import { AdviserGeneralInformation } from "./AdviserGeneralInformation"
import { AdviserLoginInformation } from "./AdviserLoginInformation"
import { AdviserPRInformation } from "./AdviserPRInformation"
import { useNavigate } from "react-router-dom"
import { useMediaQuery } from "react-responsive"
import { ShowProgress } from "src/states/showProgress"

const STEP = {
  GENERAL_INFORMATION: 1,
  LOGIN_INFORMATION: 2,
  PR_INFORMATION: 3,
}

export const AdviserRegisterPage = () => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [step, setStep] = useState<number>(STEP.GENERAL_INFORMATION)

  const navigate = useNavigate()

  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [firstNameJP, setFirstNameJP] = useState<string>("")
  const [lastNameJP, setLastNameJP] = useState<string>("")
  const [jobs, setJobs] = useState<number[]>([])
  const [otherJob, setOtherJob] = useState<string>("")
  const [birthday, setBirthday] = useState<Dayjs | null>(null)
  const [gender, setGender] = useState<string>(GENDER.MALE)
  const [nation, setNation] = useState<string>("日本")
  const [phoneNumber, setPhoneNumber] = useState<string>("")
  const [areaId, setAreaId] = useState<number>(1)
  const [avatarPath, setAvatarPath] = useState<string>("")
  const [postCode, setPostCode] = useState<string>("")
  const [province, setProvince] = useState<string>("北海道")
  const [city, setCity] = useState<string>("")
  const [street, setStreet] = useState<string>("")
  const [building, setBuilding] = useState<string>("")
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [jobValidationMessage, setJobValidationMessage] = useState<string>("")
  const [birthdayValidationMessage, setBirthdayValidationMessage] = useState<string>("")
  const [phoneNumberValidationMessage, setPhoneNumberValidationMessage] = useState<string>("")
  const [avatarPathValidationMessage, setAvatarPathValidationMessage] = useState<string>("")
  const [postCodeValidationMessage, setPostCodeValidationMessage] = useState<string>("")
  const [cityValidationMessage, setCityValidationMessage] = useState<string>("")

  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [passwordConfirm, setPasswordConfirm] = useState<string>("")
  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")
  const [passwordConfirmValidationMessage, setPasswordConfirmValidationMessage] =
    useState<string>("")

  const [abstract, setAbastrct] = useState<string>("")
  const [message, setMessage] = useState<string>("")
  const [career, setCareer] = useState<Career[]>([
    { year: new Date().getFullYear(), month: new Date().getMonth() + 1, career: "" } as Career,
  ])
  const [abstractValidationMessage, setAbstractValidationMessage] = useState<string>("")
  const [messageValidationMessage, setMessageValidationMessage] = useState<string>("")
  const [careerValidationMessage, setCareerValidationMessage] = useState<string>("")

  const [lounge, setLounge] = useState<boolean>(false)
  const [loungeValidationMessage, setLoungeValidationMessage] = useState<string>("")

  const [resident, setResident] = useState<boolean>(false)
  const [residentValidationMessage, setResidentValidationMessage] = useState<string>("")

  useEffect(() => {
    setShowHeader(false)
  })

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step])

  const handlePostCodeInput = () => {
    if (!postCode) {
      return
    }

    AddressService.autoCompleteAddressByZipCode(postCode).then((res) => {
      if (res.prefecture) {
        setProvince(res.prefecture as string)
      }

      if (res.city) {
        let city = res.city as string
        if (res.townCandidates && res.townCandidates.length >= 1) {
          city = `${city}${res.townCandidates[0]}`
        }
        setCity(city)
      }
    })
  }

  const confirmGeneralInformation = async () => {
    if (
      !(await validateGeneralInformation(
        firstName,
        lastName,
        firstNameJP,
        lastNameJP,
        jobs,
        birthday ? birthday?.format("YYYY-MM-DD") : "",
        phoneNumber,
        avatarPath,
        postCode,
        city,
        setNameValidationMessage,
        setJobValidationMessage,
        setBirthdayValidationMessage,
        setPhoneNumberValidationMessage,
        setAvatarPathValidationMessage,
        setPostCodeValidationMessage,
        setCityValidationMessage,
        t
      ))
    ) {
      return
    }

    setStep(STEP.LOGIN_INFORMATION)
  }

  const confirmLoginInformation = async () => {
    if (
      !(await validateLoginInformation(
        email,
        password,
        passwordConfirm,
        setEmailValidationMessage,
        setPasswordValidationMessage,
        setPasswordConfirmValidationMessage,
        t
      ))
    ) {
      return
    }

    setStep(STEP.PR_INFORMATION)
  }

  const handleRegister = async () => {
    if (
      !(await validatePRInformation(
        abstract,
        setAbstractValidationMessage,
        message,
        setMessageValidationMessage,
        career,
        setCareerValidationMessage,
        lounge,
        setLoungeValidationMessage,
        resident,
        setResidentValidationMessage,
        t
      ))
    ) {
      return
    }

    setShowProgress(true)
    AuthenticationService.signUp({
      email: email,
      password: password,
      userType: MEMBER_TYPE.ADVISER,
    })
      .then((res) => {
        if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
          ProfileService.createAdviser({
            userId: res.data?.UserSub as string,
            email: email,
            photoUrl: avatarPath,
            firstName: firstName,
            lastName: lastName,
            firstNameKana: firstNameJP,
            lastNameKana: lastNameJP,
            jobTitleId: jobs.join(","),
            otherJobTitle: otherJob,
            gender: gender,
            birthday: birthday?.format("YYYY-MM-DD") as string,
            country: nation,
            areaId: areaId,
            phoneNumber: phoneNumber,
            zipCode: postCode,
            province: province,
            city: city,
            streetNo: street,
            building: building,
            pr: abstract,
            message: message,
            career: career,
            isLoungeMember: lounge,
            isResident: resident,
            comment: "ADVISER REGISTER",
          })
            .then((res) => {
              setShowProgress(false)
              if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
                setShowToast({ show: true, text: t("success"), type: SNACKBAR_TYPES.SUCCESS })
                navigate("/mypage/users/list/adviser")
                navigate(0)
              } else {
                setShowProgress(false)
                setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
              }
            })
            .catch(() => {
              setShowProgress(false)
              setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
            })
        } else if (res.status === RESPONSE_CODE.CONFLICT) {
          setShowProgress(false)
          setShowToast({ show: true, text: t("already_registered"), type: SNACKBAR_TYPES.WARNING })
        } else {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  return (
    <Box
      sx={{
        ...(isTablet
          ? { height: "auto", pt: "0px", pl: "20px" }
          : { width: "100%", position: "absolute", top: 60, bottom: 120 }),
        overflowY: "scroll",
      }}
    >
      <Box sx={{ px: "20px", pt: "20px" }}>
        <Typography.Large
          color="textPrimary"
          sx={{
            mb: "40px",
          }}
        >
          {t("signup_adviser", { step: step })}
        </Typography.Large>
        <Stepper
          steps={[t("adviser_step_1"), t("adviser_step_2"), t("adviser_step_3")]}
          current={step}
          sx={{ mb: "60px" }}
        ></Stepper>
        {step === STEP.GENERAL_INFORMATION && (
          <AdviserGeneralInformation
            firstName={firstName}
            lastName={lastName}
            firstNameJP={firstNameJP}
            lastNameJP={lastNameJP}
            handleFirstName={setFirstName}
            handleLastName={setLastName}
            handleFirstNameJP={setFirstNameJP}
            handleLastNameJP={setLastNameJP}
            jobValues={jobs}
            handleJobs={setJobs}
            otherJob={otherJob}
            handleOtherJob={setOtherJob}
            birthday={birthday}
            handleBirthday={setBirthday}
            gender={gender}
            handleGener={setGender}
            nation={nation}
            handleNation={setNation}
            areaId={areaId}
            handleAreaId={setAreaId}
            avatar={avatarPath}
            handleAvatar={setAvatarPath}
            phoneNumber={phoneNumber}
            handlePhoneNumber={setPhoneNumber}
            postCode={postCode}
            handlePostCode={setPostCode}
            handlePostCodeInput={handlePostCodeInput}
            province={province}
            handleProvince={setProvince}
            city={city}
            handleCity={setCity}
            street={street}
            handleStreet={setStreet}
            building={building}
            handleBuilding={setBuilding}
            nameValidationMessage={nameValidationMessage}
            jobValidationMessage={jobValidationMessage}
            birthdayValidationMessage={birthdayValidationMessage}
            phoneNumberValidationMessage={phoneNumberValidationMessage}
            avatarPathValidationMessage={avatarPathValidationMessage}
            postCodeValidationMessage={postCodeValidationMessage}
            cityValidationMessage={cityValidationMessage}
            handleNext={() => confirmGeneralInformation()}
          />
        )}
        {step === STEP.LOGIN_INFORMATION && (
          <AdviserLoginInformation
            email={email}
            password={password}
            passwordConfirm={passwordConfirm}
            handleEmail={setEmail}
            handlePassword={setPassword}
            handlePasswordConfirm={setPasswordConfirm}
            emailValidationMessage={emailValidationMessage}
            passwordValidationMessage={passwordValidationMessage}
            passwordConfirmValidationMessage={passwordConfirmValidationMessage}
            handlePrev={() => setStep(STEP.GENERAL_INFORMATION)}
            handleNext={() => confirmLoginInformation()}
          />
        )}
        {step === STEP.PR_INFORMATION && (
          <AdviserPRInformation
            abstract={abstract}
            handleAbstract={setAbastrct}
            message={message}
            handleMessage={setMessage}
            career={career}
            handleCareer={setCareer}
            abstractValidationMessage={abstractValidationMessage}
            messageValidationMessage={messageValidationMessage}
            careerValidationMessage={careerValidationMessage}
            lounge={lounge}
            handleLounge={setLounge}
            loungeValidationMessage={loungeValidationMessage}
            resident={resident}
            handleResident={setResident}
            residentValidationMessage={residentValidationMessage}
            handlePrev={() => setStep(STEP.LOGIN_INFORMATION)}
            handleNext={() => handleRegister()}
          />
        )}
      </Box>
    </Box>
  )
}
